import React, { useEffect, useState, useCallback } from "react";
import { Plus, X, Pencil, Trash2, TrendingUp, Users, MessageCircle, Share2, Target } from "lucide-react";
import { toast } from "sonner";
import OutreachSection from "./OutreachSection";
import ContactsSection from "./ContactsSection";

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  { id: "dm", label: "DM" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "facebook_group", label: "Facebook Group" },
  { id: "instagram", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "Twitter" },
  { id: "email", label: "Email" },
  { id: "other", label: "Other" },
];

const ACTIVITY_TYPES = [
  { id: "post", label: "Post" },
  { id: "message", label: "Message" },
  { id: "comment", label: "Comment" },
  { id: "outreach", label: "Outreach" },
  { id: "share", label: "Share" },
  { id: "other", label: "Other" },
];

const LEAD_STATUSES = [
  { id: "new", label: "New", color: "#3B82F6" },
  { id: "contacted", label: "Contacted", color: "#F59E0B" },
  { id: "interested", label: "Interested", color: "#8B5CF6" },
  { id: "proposal_sent", label: "Proposal Sent", color: "#EC4899" },
  { id: "converted", label: "Converted", color: "#10B981" },
  { id: "lost", label: "Lost", color: "#6B7280" },
];

const inputCls = "w-full rounded-lg border border-[#26201625] bg-white/80 px-3.5 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";

function channelLabel(id) {
  return CHANNELS.find((c) => c.id === id)?.label || id;
}
function statusObj(id) {
  return LEAD_STATUSES.find((s) => s.id === id) || { label: id, color: "#8A7E6B" };
}

/* ── Stats Overview ──────────────────────────────────── */

function StatsOverview({ stats }) {
  if (!stats) return null;
  const cards = [
    { icon: Share2, label: "Activities", value: stats.total_activities },
    { icon: Target, label: "Leads tracked", value: stats.total_leads },
    { icon: TrendingUp, label: "Leads generated", value: stats.leads_from_activities },
    { icon: Users, label: "Converted", value: stats.leads_by_status?.converted || 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="marketing-stats">
      {cards.map(({ icon: Icon, label, value }) => (
        <div key={label} className="rp-foam bg-white/55 p-5 flex items-center gap-4" data-testid={`marketing-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(181,121,63,0.12)" }}>
            <Icon size={17} className="text-[#B5793F]" />
          </span>
          <span>
            <span className="rp-display block text-2xl font-medium text-[#262016] leading-none">{value}</span>
            <span className="block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] mt-1.5">{label}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Activity Form ───────────────────────────────────── */

function ActivityForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { channel: "whatsapp", activity_type: "post", status: "done", date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const isEdit = Boolean(initial?.id);
      const r = await fetch(`${API}/api/admin/marketing/activities${isEdit ? `/${initial.id}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      toast.success(isEdit ? "Activity updated" : "Activity logged");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 md:p-8 mb-6" data-testid="activity-form">
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{initial?.id ? "Edit activity" : "Log activity"}</p>
        <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Channel</label>
          <select value={form.channel || ""} onChange={(e) => set("channel", e.target.value)} data-testid="activity-channel" className={inputCls}>
            {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select value={form.activity_type || ""} onChange={(e) => set("activity_type", e.target.value)} data-testid="activity-type" className={inputCls}>
            {ACTIVITY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Title</label>
          <input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Posted in UI/UX Designers FB group" data-testid="activity-title" className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Description / Notes</label>
          <textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="activity-description" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Platform detail (group name, handle, etc.)</label>
          <input value={form.platform_detail || ""} onChange={(e) => set("platform_detail", e.target.value)} data-testid="activity-platform" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Date</label>
          <input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="activity-date" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Leads generated</label>
          <input type="number" min="0" value={form.leads_generated ?? 0} onChange={(e) => set("leads_generated", parseInt(e.target.value) || 0)} data-testid="activity-leads" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={form.status || "done"} onChange={(e) => set("status", e.target.value)} data-testid="activity-status" className={inputCls}>
            <option value="planned">Planned</option>
            <option value="done">Done</option>
          </select>
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="activity-save-btn"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
        {busy ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

/* ── Lead Form ───────────────────────────────────────── */

function LeadForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { status: "new", source_channel: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const isEdit = Boolean(initial?.id);
      const r = await fetch(`${API}/api/admin/marketing/leads${isEdit ? `/${initial.id}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      toast.success(isEdit ? "Lead updated" : "Lead added");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 md:p-8 mb-6" data-testid="lead-form">
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{initial?.id ? "Edit lead" : "Add lead"}</p>
        <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name</label>
          <input value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Lead name" data-testid="lead-name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Contact (email / phone / handle)</label>
          <input value={form.contact || ""} onChange={(e) => set("contact", e.target.value)} placeholder="e.g. john@company.com" data-testid="lead-contact" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Source channel</label>
          <select value={form.source_channel || ""} onChange={(e) => set("source_channel", e.target.value)} data-testid="lead-source" className={inputCls}>
            <option value="">Not specified</option>
            {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={form.status || "new"} onChange={(e) => set("status", e.target.value)} data-testid="lead-status" className={inputCls}>
            {LEAD_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Notes</label>
          <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="lead-notes" className={inputCls} />
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="lead-save-btn"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
        {busy ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────── */

export default function AdminMarketing() {
  const [view, setView] = useState("contacts");
  const [activities, setActivities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);

  const loadActivities = useCallback(() => {
    fetch(`${API}/api/admin/marketing/activities`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setActivities)
      .catch(() => setActivities([]));
  }, []);

  const loadLeads = useCallback(() => {
    fetch(`${API}/api/admin/marketing/leads`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setLeads)
      .catch(() => setLeads([]));
  }, []);

  const loadStats = useCallback(() => {
    fetch(`${API}/api/admin/marketing/stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  useEffect(() => {
    loadActivities();
    loadLeads();
    loadStats();
  }, [loadActivities, loadLeads, loadStats]);

  const reload = () => {
    loadActivities();
    loadLeads();
    loadStats();
    setEditing(null);
  };

  const deleteActivity = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    const r = await fetch(`${API}/api/admin/marketing/activities/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); reload(); } else toast.error("Delete failed");
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    const r = await fetch(`${API}/api/admin/marketing/leads/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); reload(); } else toast.error("Delete failed");
  };

  return (
    <div data-testid="admin-marketing">
      <StatsOverview stats={stats} />

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6" data-testid="marketing-sub-tabs">
        {[{ id: "contacts", label: "Contacts" }, { id: "activities", label: "Activities" }, { id: "leads", label: "Leads" }, { id: "outreach", label: "Outreach" }].map((t) => (
          <button key={t.id} onClick={() => { setView(t.id); setEditing(null); }} data-testid={`marketing-tab-${t.id}`}
                  className="px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all"
                  style={{
                    background: t.id === view ? "#262016" : "transparent",
                    color: t.id === view ? "#EDE7D9" : "#262016",
                    border: "1px solid " + (t.id === view ? "#262016" : "rgba(38,32,22,0.2)"),
                  }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Activities View ─────────────────────────── */}
      {view === "activities" && (
        <>
          {editing !== null && editing._type === "activity" ? (
            <ActivityForm initial={editing.id ? editing : null} onSaved={reload} onCancel={() => setEditing(null)} />
          ) : (
            <button onClick={() => setEditing({ _type: "activity" })} data-testid="marketing-add-activity-btn"
                    className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5]"
                    style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
              <Plus size={15} /> Log activity
            </button>
          )}

          <div className="space-y-4" data-testid="marketing-activities-list">
            {activities.length === 0 && <p className="rp-foam bg-white/45 p-8 text-center text-sm text-[#8A7E6B]">No activities logged yet. Start tracking your marketing efforts.</p>}
            {activities.map((a) => (
              <div key={a.id} className="rp-foam bg-white/55 p-5 flex items-start gap-4" data-testid={`activity-${a.id}`}>
                <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: a.status === "planned" ? "rgba(59,130,246,0.12)" : "rgba(16,185,129,0.12)" }}>
                  <MessageCircle size={16} className={a.status === "planned" ? "text-blue-500" : "text-emerald-600"} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="rp-display text-base md:text-lg font-medium text-[#262016]">{a.title}</p>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-[#262016] text-[#EDE7D9]">
                      {channelLabel(a.channel)}
                    </span>
                    <span className="text-[11px] text-[#8A7E6B] uppercase tracking-wide self-center">{a.activity_type}</span>
                    {a.platform_detail && <span className="text-[11px] text-[#8A7E6B] self-center">- {a.platform_detail}</span>}
                    {a.date && <span className="text-[11px] text-[#8A7E6B] self-center">{a.date}</span>}
                    {a.leads_generated > 0 && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#B5793F]/15 text-[#B5793F]">
                        {a.leads_generated} lead{a.leads_generated > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {a.description && <p className="text-sm text-[#5C5042] mt-2">{a.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setEditing({ ...a, _type: "activity" })} data-testid={`activity-edit-${a.id}`}
                          className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:border-[#262016]">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteActivity(a.id)} data-testid={`activity-delete-${a.id}`}
                          className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Leads View ──────────────────────────────── */}
      {view === "leads" && (
        <>
          {editing !== null && editing._type === "lead" ? (
            <LeadForm initial={editing.id ? editing : null} onSaved={reload} onCancel={() => setEditing(null)} />
          ) : (
            <button onClick={() => setEditing({ _type: "lead" })} data-testid="marketing-add-lead-btn"
                    className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5]"
                    style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
              <Plus size={15} /> Add lead
            </button>
          )}

          {/* Lead pipeline summary */}
          {stats && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="lead-pipeline">
              {LEAD_STATUSES.map((s) => {
                const count = stats.leads_by_status?.[s.id] || 0;
                return (
                  <span key={s.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold"
                        style={{ background: s.color + "18", color: s.color, border: `1px solid ${s.color}30` }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    {s.label}: {count}
                  </span>
                );
              })}
            </div>
          )}

          <div className="space-y-4" data-testid="marketing-leads-list">
            {leads.length === 0 && <p className="rp-foam bg-white/45 p-8 text-center text-sm text-[#8A7E6B]">No leads tracked yet. Add leads from your marketing efforts.</p>}
            {leads.map((l) => {
              const st = statusObj(l.status);
              return (
                <div key={l.id} className="rp-foam bg-white/55 p-5 flex items-start gap-4" data-testid={`lead-${l.id}`}>
                  <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-sm font-bold"
                        style={{ background: st.color }}>
                    {(l.name || "?")[0].toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="rp-display text-base md:text-lg font-medium text-[#262016]">{l.name}</p>
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: st.color + "18", color: st.color }}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1.5 text-[12px] text-[#8A7E6B]">
                      {l.contact && <span>{l.contact}</span>}
                      {l.source_channel && <span>via {channelLabel(l.source_channel)}</span>}
                    </div>
                    {l.notes && <p className="text-sm text-[#5C5042] mt-2">{l.notes}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditing({ ...l, _type: "lead" })} data-testid={`lead-edit-${l.id}`}
                            className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:border-[#262016]">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteLead(l.id)} data-testid={`lead-delete-${l.id}`}
                            className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Contacts View ──────────────────────────────── */}
      {view === "contacts" && <ContactsSection />}

      {/* ── Outreach View ──────────────────────────────── */}
      {view === "outreach" && <OutreachSection />}
    </div>
  );
}
